/**
 * Frontend prediction module — wraps the shared V2.2 algorithm used by Lambdas.
 *
 * Source of truth: amplify/functions/shared/prediction.ts
 * UI categories use Title Case (Vivid/Great/…); backend uses UPPERCASE.
 */
import {
  calculateQualityScore as calculateQualityScoreShared,
  calculateQualityScoreV1 as calculateQualityScoreV1Shared,
  calculateQualityScoreV2 as calculateQualityScoreV2Shared,
  calculateQualityScoreV2_1 as calculateQualityScoreV2_1Shared,
  calculateQualityScoreV2_2 as calculateQualityScoreV2_2Shared,
  type PredictionInput as SharedPredictionInput,
  type QualityPrediction as SharedQualityPrediction,
  type AlgorithmVersion,
  type QualityCategory as BackendQualityCategory,
} from '../../amplify/functions/shared/prediction';

export type QualityCategory = 'Poor' | 'Fair' | 'Good' | 'Great' | 'Vivid';

export type PredictionInput = SharedPredictionInput;

export interface QualityPrediction {
  score: number;
  category: QualityCategory;
  factors: SharedQualityPrediction['factors'];
  explanation: string;
  algorithmVersion?: AlgorithmVersion;
  factorBreakdown?: SharedQualityPrediction['factorBreakdown'];
}

function toUiCategory(category: BackendQualityCategory): QualityCategory {
  switch (category) {
    case 'VIVID':
      return 'Vivid';
    case 'GREAT':
      return 'Great';
    case 'GOOD':
      return 'Good';
    case 'FAIR':
      return 'Fair';
    case 'POOR':
    default:
      return 'Poor';
  }
}

function toUiResult(result: SharedQualityPrediction): QualityPrediction {
  return {
    score: result.score,
    category: toUiCategory(result.category),
    factors: result.factors,
    explanation: result.explanation,
    algorithmVersion: result.algorithmVersion,
    factorBreakdown: result.factorBreakdown,
  };
}

/**
 * Default quality score — shared V2.2 (same as Lambdas).
 */
export function calculateQualityScore(
  input: PredictionInput,
  date?: Date,
  version: AlgorithmVersion = 'v2.2'
): QualityPrediction {
  return toUiResult(calculateQualityScoreShared(input, date, version));
}

export function calculateQualityScoreV1(input: PredictionInput): QualityPrediction {
  return toUiResult(calculateQualityScoreV1Shared(input));
}

export function calculateQualityScoreV2(input: PredictionInput, date?: Date): QualityPrediction {
  return toUiResult(calculateQualityScoreV2Shared(input, date));
}

export function calculateQualityScoreV2_1(input: PredictionInput, date?: Date): QualityPrediction {
  return toUiResult(calculateQualityScoreV2_1Shared(input, date));
}

export function calculateQualityScoreV2_2(input: PredictionInput, date?: Date): QualityPrediction {
  return toUiResult(calculateQualityScoreV2_2Shared(input, date));
}

/**
 * Get color for quality category (for UI display)
 * @deprecated Use getQualityHexColor from @/lib/qualityScore instead for consistency
 */
export { getQualityHexColor as getQualityColor } from '@/lib/qualityScore';

/**
 * Get description for quality category
 * @deprecated Use getQualityDescription from @/lib/qualityScore instead for consistency
 */
export { getQualityDescription } from '@/lib/qualityScore';
